/**
 * Shared helpers for the Rebuy widget callbacks
 * (rebuy/gwp-callback-products-change.js, rebuy/sample-callback-products-change.js,
 * rebuy/widget-callback-products-change.js).
 * Loaded globally so those dashboard-pasted callbacks can call into it as window.RebuySharedHelpers.
 */
function hasItemInCart(id) {
  return Rebuy.SmartCart.items().some((item) => item.properties?._widget_id === id);
}

/**
 * Injects a stock delivery status message (available / continue / sold out) into a
 * product block, matching the same states and classes used in the cart drawer's
 * rebuy-cart-items.vue, driven by window.stockDeliveryTimes.
 * @param {string[]} anchorSelectors - tried in order to find the element the status is inserted after.
 */
function addInventoryStatus(infoEl, product, anchorSelectors) {
  if (infoEl.querySelector('.rebuy-cart__flyout-item-inventory-status')) return;

  const variant = product?.selected_variant || product?.variants?.[0];
  if (!variant) return;

  const quantity = variant.inventory_quantity;
  const policy = variant.inventory_policy;

  let modifier;
  let text;
  if (quantity > 0) {
    modifier = 'available';
    text = window.stockDeliveryTimes?.available || 'Available';
  } else if (policy === 'continue') {
    modifier = 'continue';
    text = `${quantity} ${window.stockDeliveryTimes?.continue || 'Continue'}`;
  } else {
    modifier = 'sold-out';
    text = `${quantity} ${window.stockDeliveryTimes?.soldOut || 'Sold out'}`;
  }

  const statusEl = document.createElement('p');
  statusEl.className = `rebuy-cart__flyout-item-inventory-status rebuy-cart__flyout-item-inventory-status--${modifier}`;
  statusEl.textContent = text;

  const anchorEl = anchorSelectors.reduce(
    (found, selector) => found || infoEl.querySelector(selector),
    null
  );
  (anchorEl || infoEl).insertAdjacentElement('afterend', statusEl);
}

/**
 * Injects a pipe-separated ingredients message into a product block, sourced from the
 * `metafields.details.ingredients` list metafield (e.g. size/price variants for hair dye).
 */
function addIngredientMessage(infoEl, product) {
  if (infoEl.querySelector('.rebuy-cart__flyout-item-ingredients')) return;

  const ingredients = product?.metafields?.details?.ingredients?.value;
  if (!Array.isArray(ingredients) || ingredients.length === 0) return;

  const messageEl = document.createElement('p');
  messageEl.className = 'rebuy-cart__flyout-item-ingredients';
  messageEl.textContent = ingredients.join(' | ');
  infoEl.insertAdjacentElement('beforeend', messageEl);
}

/**
 * Fetches a product's variants via Shopify's Ajax product endpoint (widget.data.products
 * mirrors the Admin REST resource, which never carries unit_price_measurement) and injects
 * the unit price line after `.rebuy-product-price`, matching rebuy-cart-items.vue's format.
 */
async function addUnitPriceMeasurement(infoEl, product) {
  const priceEl = infoEl.querySelector('.rebuy-product-price');
  if (!priceEl || !product?.handle || infoEl.querySelector('.rebuy-cart__flyout-item-unit-price')) {
    return;
  }

  const variant = product?.selected_variant || product?.variants?.[0];
  if (!variant?.id) return;

  const response = await fetch(`/products/${product.handle}.js`);
  const fetchedProduct = await response.json();
  const fetchedVariant = fetchedProduct?.variants?.find((v) => v.id === variant.id);
  const unitPriceMeasurement = fetchedVariant?.unit_price_measurement;

  if (!unitPriceMeasurement || typeof fetchedVariant.unit_price !== 'number') return;
  const unitPriceEl = document.createElement('p');
  unitPriceEl.className = 'rebuy-cart__flyout-item-unit-price';
  unitPriceEl.textContent = `${Math.round(unitPriceMeasurement.quantity_value)}${unitPriceMeasurement.quantity_unit} (${Rebuy.Cart.formatMoney(fetchedVariant.unit_price)} / ${unitPriceMeasurement.reference_value}${unitPriceMeasurement.reference_unit})`;
  priceEl.insertAdjacentElement('afterend', unitPriceEl);
}

function syncLockedState(guardName, guard) {
  document.querySelectorAll('[id^="rebuy-widget-"]').forEach((widgetEl) => {
    const id = widgetEl.id.replace('rebuy-widget-', '');
    if (!guard.widgetIds.has(id)) return;

    widgetEl.classList.toggle('gwp-locked', guard.lockedIds.has(id));
  });
}

/**
 * Creates (or returns the existing) click-guard state for a widget family, keyed by name
 * (e.g. 'gwp', 'sample') so different widget types don't share lock state. Installs the
 * click-guard listener and cart-change listener once per name.
 * @param {Function} [options.onCartChange] - extra logic to run after each cart-change sync.
 */
function createWidgetGuard(name, { onCartChange } = {}) {
  if (!window.RebuySharedHelpers.guards) {
    window.RebuySharedHelpers.guards = {};
  }

  if (window.RebuySharedHelpers.guards[name]) {
    return window.RebuySharedHelpers.guards[name];
  }

  const guard = { widgetIds: new Set(), lockedIds: new Set() };
  window.RebuySharedHelpers.guards[name] = guard;

  // Capture phase so Rebuy's own handler never runs once locked
  document.addEventListener(
    'click',
    (event) => {
      const button = event.target.closest('.rebuy-product-block .rebuy-button');
      if (!button) return;

      const widgetEl = button.closest('[id^="rebuy-widget-"]');
      if (!widgetEl) return;

      const clickedId = widgetEl.id.replace('rebuy-widget-', '');
      if (!guard.widgetIds.has(clickedId)) return;

      if (guard.lockedIds.has(clickedId)) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }

      guard.lockedIds.add(clickedId);
      widgetEl.classList.add('gwp-locked');
    },
    true
  );

  document.addEventListener('rebuy:cart.change', () => {
    guard.widgetIds.forEach((id) => {
      if (hasItemInCart(id)) {
        guard.lockedIds.add(id);
      } else {
        guard.lockedIds.delete(id);
      }
    });
    syncLockedState(name, guard);
    onCartChange?.();
  });

  return guard;
}

/**
 * Registers a widget with its guard and syncs its initial locked state, replacing the
 * repeated bottom block in each callback file.
 */
function initWidgetLock(guard, widgetId) {
  guard.widgetIds.add(widgetId);

  if (hasItemInCart(widgetId)) {
    guard.lockedIds.add(widgetId);
  } else {
    guard.lockedIds.delete(widgetId);
  }

  const widgetEl = document.querySelector(`#rebuy-widget-${widgetId}`);
  if (widgetEl) {
    widgetEl.classList.toggle('gwp-locked', guard.lockedIds.has(widgetId));
  }
}

window.RebuySharedHelpers = {
  hasItemInCart,
  addInventoryStatus,
  addIngredientMessage,
  addUnitPriceMeasurement,
  syncLockedState,
  createWidgetGuard,
  initWidgetLock,
};
